// ═══════════════════════════════════════════════════════════════
// SidebarChronicles — Fil d'actualité ITRIIA dans la sidebar
// Rétractable, premium, partagé par tous les users.
// L'état ouvert/fermé persiste via localStorage.
// Gracieusement silencieux si backend absent.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import {
  ChevronRight, Scroll, Users, Brain, Hammer, Languages, Sparkles,
  BookOpen, Shield, Globe, Layers, FlaskConical, Target, MessagesSquare,
  Share2, Lock, Compass, Wand2, FileStack,
} from 'lucide-react';
import './SidebarChronicles.css';

/* Mapping icône string → composant Lucide. Accepte les deux conventions :
 * lowercase historique ('users', 'brain'…) ET PascalCase (le vrai nom Lucide
 * 'FileStack', 'Compass'…) pour ne pas tomber en fallback Sparkles. */
const ICON_MAP = {
  // Lowercase (historique)
  users: Users, brain: Brain, hammer: Hammer, languages: Languages,
  sparkles: Sparkles, book: BookOpen, shield: Shield, globe: Globe,
  layers: Layers, flask: FlaskConical, target: Target, chat: MessagesSquare,
  share: Share2, lock: Lock, compass: Compass, magic: Wand2,
  // PascalCase (noms Lucide officiels)
  Users, Brain, Hammer, Languages, Sparkles, BookOpen, Shield, Globe,
  Layers, FlaskConical, Target, MessagesSquare, Share2, Lock, Compass, Wand2,
  FileStack,
};
function ChronIcon({ name, size = 13 }) {
  const Cmp = ICON_MAP[name] || Sparkles;
  return <Cmp size={size} strokeWidth={1.7} />;
}

const MONTHS_FR = [
  'janv.', 'févr.', 'mars', 'avril', 'mai', 'juin',
  'juill.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

function formatShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}

function dateKey(e) {
  return e?.publish_date || e?.event_date || '';
}

const STORAGE_KEY = 'oracle_sidebar_chronicles_open';
const SEEN_KEY = 'oracle_sidebar_chronicles_last_seen';

export default function SidebarChronicles({ limit = 5 }) {
  const [entries, setEntries] = useState(null);
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, open ? '1' : '0');
      if (open && entries?.length) {
        localStorage.setItem(SEEN_KEY, dateKey(entries[0]));
      }
    } catch { /* silent */ }
  }, [open, entries]);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('oracle_token');
    if (!token) { setErrored(true); return; }
    fetch(`/api/chronicles?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(list => { if (alive) setEntries(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setErrored(true); });
    return () => { alive = false; };
  }, [limit]);

  if (errored || !entries || entries.length === 0) return null;

  const lastSeen = (() => {
    try { return localStorage.getItem(SEEN_KEY) || ''; } catch { return ''; }
  })();
  const hasNew = lastSeen !== dateKey(entries[0]);

  return (
    <div className={`sc-wrap ${open ? 'sc-open' : ''}`}>
      <button
        className="sc-toggle"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <Scroll size={14} strokeWidth={1.75} className="sc-icon" />
        <span className="sc-title">Chroniques</span>
        {hasNew && !open && <span className="sc-dot" aria-label="Nouveauté" />}
        <ChevronRight size={14} strokeWidth={2} className={`sc-chevron ${open ? 'rot' : ''}`} />
      </button>

      {open && (
        <ul className="sc-list">
          {entries.map((e, idx) => (
            <li key={e.id || idx} className={`sc-item ${idx === 0 ? 'sc-fresh' : ''}`}>
              <span className="sc-date">{formatShort(dateKey(e))}</span>
              <span className="sc-entry-icon"><ChronIcon name={e.icon} /></span>
              <div className="sc-body">
                <div className="sc-headline">{e.title}</div>
                {(e.teaser || e.subtitle) && (
                  <div className="sc-subtitle">{e.teaser || e.subtitle}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
